'use client';

import { useStore } from '@/stores/store';
import axios from '@/utils/axios';
import { notifications } from '@mantine/notifications';
import useRollbar from '@/hooks/useRollbar';
import { ApiConstants, ErrorType, Keys } from './useApi';
import { AxiosError } from 'axios';

export type PostOptions<K> = {
  error: {
    message: string;
    showNotification?: boolean;
    shouldSkipLogging?: (error: Error | AxiosError) => boolean;
  };
  urlParams?: string;
  onSuccess?: (data: K) => void;
  onError?: (error?: ErrorType) => void;
};

function usePost<T>(key: Keys) {
  const updateData = useStore((store) => store.updateData);

  const rollbar = useRollbar();

  async function post<K>(
    data: T,
    options: PostOptions<K>,
    setLoading: (loading: boolean) => void,
    setError: (error?: ErrorType) => void,
  ) {
    setLoading(true);
    setError(undefined);

    try {
      const res = await axios.post<K>(
        `${ApiConstants[key].url}${options.urlParams ? `${options.urlParams}` : ''}`,
        data,
      );
      const entity = ApiConstants[key].entity;
      if (entity) {
        updateData<K[]>(entity, (state) => [...(state && state.length > 0 ? state : []), res.data]);
      }
      setLoading(false);
      options.onSuccess?.(res.data);
    } catch (error) {
      // Log anterior: https://app.rollbar.com/a/AppMasters/fix/item/Assistant/1609/occurrence/439705799065#detail
      const err = error as AxiosError<{ message: string }>;
      const errorMsg = err.response?.data?.message || err.message || 'Error';
      const rollbarErrorMsg = options.error.message || errorMsg;
      setLoading(false);
      if (!options.error.shouldSkipLogging?.(error as Error)) {
        rollbar.error(rollbarErrorMsg, err, error as Error, { data, options, errorMsg });
      }
      if (options.error.showNotification || options.error.showNotification === undefined) {
        notifications.show({
          title: 'Ocorreu um erro',
          message: options.error.message,
          color: 'red',
        });
      }

      const errorObj = {
        status: err?.response?.status,
        message: errorMsg,
        data: err?.response?.data,
      };
      setError?.(errorObj);
      options.onError?.(errorObj);
    }
  }

  return { post };
}

export default usePost;
